import { Path } from "@kform/core";
import * as React from "react";

import { FormContext, FormContextValue } from "../contexts/FormContext";
import { FormatterControllerState } from "../hooks/useFormatter";
import { InputElementProps, InputOptions, useInput } from "../hooks/useInput";
import { shallow } from "../utils/shallow";

/**
 * Properties of the {@link Input} component.
 */
export type InputProps<
  T = unknown,
  TFormatted = T | undefined,
  TInput = unknown,
> = InputOptions<T, TFormatted, TInput> & InputOwnProps<T, TFormatted, TInput>;

/**
 * Own properties of the {@link Input} component.
 */
export interface InputOwnProps<
  T = unknown,
  TFormatted = T | undefined,
  TInput = unknown,
> {
  path?: Path | string;
  children: (
    elementProps: InputElementProps<TFormatted, TInput>,
    state: FormatterControllerState<T, TFormatted>,
  ) => React.ReactNode;
}

/**
 * Component used to wrap an input so that it "connects" to a form value.
 *
 * This component is a wrapper around the {@link useInput} hook.
 */
export function Input<
  T = unknown,
  TFormatted = T | undefined,
  TInput = unknown,
>({ path, children, ...props }: InputProps<T, TFormatted, TInput>) {
  const formContext = React.useContext(FormContext);
  const inputController = useInput(path, props);

  const formManager = inputController.useFormManager();
  const currentPath = inputController.usePath();
  const newFormContext = React.useMemo<FormContextValue>(
    () => ({
      formManager,
      currentPath,
      controller:
        formContext?.formManager === formManager
          ? formContext.controller
          : undefined,
    }),
    [
      currentPath,
      formContext?.controller,
      formContext?.formManager,
      formManager,
    ],
  );

  const state = inputController.useState((state) => state, {
    equalityFn: shallow,
  });
  return (
    <FormContext.Provider value={newFormContext}>
      {children(inputController.inputProps, state)}
    </FormContext.Provider>
  );
}
